<template>
    <v-dialog v-model="dialogProxy" max-width="980px" persistent scrollable>
        <v-card class="manage-brands-card">
            <v-card-title class="py-3 white--text primarycolor d-flex align-center">
                <v-icon color="white" class="mr-2">mdi-shape-outline</v-icon>
                <div>
                    <div class="text-subtitle-1 font-weight-medium">Manage Brands</div>
                    <div class="caption">Merge and split mentions</div>
                </div>
                <v-spacer />
                <v-btn icon @click="closeDialog" aria-label="Close manage brands dialog">
                    <v-icon color="white">mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pt-4 px-6 pb-2">
                <!-- <v-alert dense outlined type="info" class="mb-3">
                    Removed mentions are stored as <strong>blacklisted</strong>. Your edits are stored separately and can be sent to backend for future updates.
                </v-alert> -->

                <v-alert v-if="mergeSelectionSource" dense text type="warning" class="mb-2">
                    Selected source: <strong>{{ mergeSelectionSource }}</strong
                    >. Now choose the target brand and click <strong>Merge into this brand</strong>.
                    <v-btn small text color="warning darken-2" class="ml-2" @click="cancelMergeSelection">Cancel selection</v-btn>
                </v-alert>

                <v-row dense class="mb-1 mt-1">
                    <v-col cols="12" md="8">
                        <v-text-field v-model="brandSearchQuery" dense outlined clearable hide-details prepend-inner-icon="mdi-magnify" label="Search brands or mentions" />
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center justify-end">
                        <span class="caption grey--text text--darken-1"> {{ brandsToRender.length }} visible · {{ managedBrandsDraft.length }} total </span>
                    </v-col>
                </v-row>

                <v-row dense class="mb-2">
                    <v-col cols="12">
                        <v-btn small rounded outlined color="primary" @click="openSearchEntitiesDialog">
                            <v-icon left small>mdi-magnify-plus-outline</v-icon>
                            Add entities
                        </v-btn>
                        <!-- <span class="caption grey--text text--darken-1 ml-2"
                            >Only the top 200 brands are tracked automatically — products aren't tracked at all. Search here to add products or brands that aren't in the list yet.</span
                        > -->
                    </v-col>
                </v-row>

                <div class="manage-brands-list mt-3">
                    <v-card
                        v-for="brand in brandsToRender"
                        :key="`managed-brand-${brand.canonicalName}`"
                        outlined
                        :style="getBrandCardStyle(brand)"
                        class="mb-3 pa-3 manage-brand-card"
                        :class="{ 'manage-brand--selected-source': isMergeSource(brand.canonicalName) }">
                        <div class="d-flex align-center mb-2 brand-header">
                            <div class="font-weight-medium d-flex align-center" style="min-width: 0">
                                <template v-if="isCanonicalEditing(brand.canonicalName)">
                                    <v-text-field
                                        v-model="canonicalEditValue"
                                        dense
                                        outlined
                                        hide-details
                                        class="mr-1"
                                        style="max-width: 280px"
                                        label="Canonical name"
                                        @keyup.enter="applyCanonicalRename" />
                                    <v-btn x-small icon color="primary" @click.stop="applyCanonicalRename" title="Apply canonical name">
                                        <v-icon x-small>mdi-check</v-icon>
                                    </v-btn>
                                    <v-btn x-small icon @click.stop="cancelCanonicalEdit" title="Cancel rename">
                                        <v-icon x-small>mdi-close</v-icon>
                                    </v-btn>
                                </template>
                                <template v-else>
                                    <span class="brand-dot" :style="{ backgroundColor: getBrandAccentColor(brand) }"></span>
                                    <span class="brand-title">{{ brand.canonicalName }}</span>
                                    <v-btn x-small icon class="ml-1" @click.stop="startCanonicalEdit(brand.canonicalName)" title="Rename canonical">
                                        <v-icon x-small>mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn x-small icon class="ml-1" v-bind="attrs" v-on="on" :color="isFavorite(brand) ? 'amber darken-2' : 'grey'" @click.stop="toggleFavorite(brand)">
                                                <v-icon x-small>{{ isFavorite(brand) ? "mdi-star" : "mdi-star-outline" }}</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ isFavorite(brand) ? "Remove from favorites" : "Add to favorites" }}</span>
                                    </v-tooltip>
                                    <v-menu :value="colorMenuFor === brand.canonicalName" @input="(v) => onColorMenuChange(v, brand)" :close-on-content-click="false" offset-y left>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-tooltip top>
                                                <template v-slot:activator="{ on: onTip, attrs: attrsTip }">
                                                    <v-btn x-small icon class="ml-1" v-bind="{ ...attrs, ...attrsTip }" v-on="{ ...on, ...onTip }" @click.stop>
                                                        <span class="brand-color-swatch" :style="{ backgroundColor: getBrandAccentColor(brand) }"></span>
                                                    </v-btn>
                                                </template>
                                                <span>Set chart color</span>
                                            </v-tooltip>
                                        </template>
                                        <v-card class="pa-2" width="300">
                                            <v-color-picker
                                                v-model="activeColor"
                                                dot-size="22"
                                                mode="hexa"
                                                show-swatches
                                                :swatches="colorSwatches"
                                                swatches-max-height="120"
                                                @update:color="(c) => setBrandColor(brand, c && c.hex ? c.hex : c)" />
                                            <div class="d-flex justify-space-between mt-1">
                                                <v-btn x-small text color="grey darken-1" @click="resetBrandColor(brand)">
                                                    <v-icon x-small left>mdi-restore</v-icon>
                                                    Auto
                                                </v-btn>
                                                <v-btn x-small text color="primary" @click="closeColorMenu()">Done</v-btn>
                                            </div>
                                        </v-card>
                                    </v-menu>
                                </template>
                            </div>
                            <div class="brand-header-actions">
                                <v-btn
                                    small
                                    rounded
                                    outlined
                                    :color="isMergeSource(brand.canonicalName) ? 'warning darken-2' : 'primary'"
                                    class="mr-2 merge-action-btn merge-action-btn--source"
                                    v-if="!mergeSelectionSource || isMergeSource(brand.canonicalName)"
                                    @click="startMergeSelection(brand.canonicalName)">
                                    <v-icon left small>{{ isMergeSource(brand.canonicalName) ? "mdi-check-circle" : "mdi-source-branch" }}</v-icon>
                                    {{ isMergeSource(brand.canonicalName) ? "Source selected" : "Merge into another Brand" }}
                                </v-btn>
                                <v-btn
                                    small
                                    rounded
                                    color="primary darken-1"
                                    class="mr-2 merge-action-btn merge-action-btn--target white--text"
                                    v-else
                                    @click="requestMergeWithTarget(brand.canonicalName)">
                                    <v-icon left small>mdi-call-merge</v-icon>
                                    Merge into this brand
                                </v-btn>
                                <v-btn small rounded outlined color="primary" class="mr-2 merge-action-btn add-mention-action-btn" @click="openAddMentionDialog(brand.canonicalName)">
                                    <v-icon left small>mdi-plus</v-icon>
                                    Add mention
                                </v-btn>
                                <!-- <v-chip small outlined class="mentions-count-chip">{{ (brand.mentions || []).length }} mentions</v-chip> -->
                            </div>
                        </div>

                        <div class="d-flex align-center flex-wrap mb-2 brand-domain-row">
                            <v-chip outlined color="primary" class="brand-domain-chip clickable-chip" @click="openDomainDialog(brand.canonicalName)">
                                <v-icon left x-small>{{ normalizeDomain(brand.brand_domain) ? "mdi-web-check" : "mdi-plus" }}</v-icon>
                                {{ normalizeDomain(brand.brand_domain) || "Add domain" }}
                            </v-chip>
                            <!-- <span class="caption grey--text text--darken-1 ml-2">Optional · wird ohne https:// und www gespeichert</span> -->
                        </div>

                        <div class="caption grey--text text--darken-1 mb-1">Mentions</div>
                        <div class="d-flex flex-wrap">
                            <v-chip v-for="mention in brand.mentions" :key="`mention-${brand.canonicalName}-${mention}`" small class="mr-1 mb-1 manage-mention-chip" outlined>
                                {{ mention }}
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            x-small
                                            rounded
                                            outlined
                                            color="primary"
                                            class="ml-2 split-mention-btn"
                                            v-bind="attrs"
                                            v-on="on"
                                            @click.stop="splitManagedBrandByMention(brand.canonicalName, mention)"
                                            title="Create separate brand from this mention">
                                            <v-icon x-small>mdi-call-split</v-icon> Split
                                        </v-btn>
                                    </template>
                                    <span>Split mention into own brand</span>
                                </v-tooltip>
                                <!-- <v-btn x-small icon @click.stop="removeManagedMention(brand.canonicalName, mention)" title="Blacklist mention">
                                    <v-icon x-small>mdi-close</v-icon>
                                </v-btn> -->
                            </v-chip>
                        </div>

                        <div v-if="brand.blacklisted && brand.blacklisted.length" class="mt-2">
                            <div class="caption grey--text text--darken-1 mb-1">Blacklisted</div>
                            <v-chip v-for="m in brand.blacklisted" :key="`blacklisted-${brand.canonicalName}-${m}`" x-small color="grey lighten-3" class="mr-1 mb-1">
                                {{ m }}
                            </v-chip>
                        </div>
                    </v-card>
                </div>

                <div v-if="totalBrandPages > 1" class="d-flex justify-center mt-2">
                    <v-pagination v-model="brandsPage" :length="totalBrandPages" :total-visible="7" />
                </div>
            </v-card-text>

            <v-divider />
            <v-card-actions class="px-6 py-3">
                <v-btn outlined color="error" :loading="resetLoading" :disabled="resetLoading" @click="openResetConfirmDialog">
                    <v-icon left small>mdi-delete-alert-outline</v-icon>
                    Reset Brands
                </v-btn>
                <v-spacer />
                <v-btn text @click="closeDialog">Cancel</v-btn>
                <v-btn rounded color="primary" @click="saveChanges">
                    <v-icon left small>mdi-content-save</v-icon>
                    Save edits
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog v-model="mergeConfirmDialog" max-width="520px">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-medium">Confirm merge</v-card-title>
                <v-card-text>
                    Merge <strong>{{ mergeSelectionSource }}</strong> into <strong>{{ mergeConfirmTarget }}</strong
                    >? This will move mentions and blacklisted entries into the target brand.
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="cancelMergeConfirm">Cancel</v-btn>
                    <v-btn color="primary" rounded @click="confirmMergeSelection">Confirm merge</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="addMentionDialog" max-width="520px">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-medium">Add mention</v-card-title>
                <v-card-text>
                    <div class="caption grey--text text--darken-1 mb-2">
                        Brand: <strong>{{ addMentionTarget }}</strong>
                    </div>
                    <v-text-field
                        v-model="addMentionValue"
                        dense
                        outlined
                        clearable
                        prepend-inner-icon="mdi-plus"
                        label="Mention"
                        placeholder="e.g. douglas beauty club"
                        @keyup.enter="confirmAddMention" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="cancelAddMention">Cancel</v-btn>
                    <v-btn color="primary" rounded @click="confirmAddMention">Add</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="domainDialog" max-width="520px">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-medium">Set domain</v-card-title>
                <v-card-text>
                    <div class="caption grey--text text--darken-1 mb-2">
                        Brand: <strong>{{ domainTarget }}</strong>
                    </div>
                    <v-text-field
                        v-model="domainValue"
                        dense
                        outlined
                        clearable
                        prepend-inner-icon="mdi-web"
                        label="Domain"
                        placeholder="example.com"
                        hint="Wird normalisiert gespeichert (ohne https:// und www)"
                        persistent-hint
                        @keyup.enter="confirmDomain" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="cancelDomain">Cancel</v-btn>
                    <v-btn color="primary" rounded @click="confirmDomain">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="resetConfirmDialog" max-width="560px">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-medium">Reset Brands</v-card-title>
                <v-card-text>
                    This will remove all saved brand rules for this project and rebuild mapping on the next update.<br />
                    <strong>Brand Visibility data will not be deleted.</strong>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text :disabled="resetLoading" @click="resetConfirmDialog = false">Cancel</v-btn>
                    <v-btn color="error" rounded :loading="resetLoading" :disabled="resetLoading" @click="confirmResetBrands">Reset now</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <SearchResponseEntitiesDialog
            v-model="searchEntitiesDialog"
            :project-id="projectId"
            :managed-brands="managedBrandsDraft"
            @add-brands="onSearchEntitiesAddBrands"
            @add-merged-brand="onSearchEntitiesAddMergedBrand" />
    </v-dialog>
</template>

<script>
    import { resolveBrandColor } from "../../utils/brandColor"
    import SearchResponseEntitiesDialog from "./SearchResponseEntitiesDialog.vue"

    export default {
        name: "ManageBrandsDialog",
        components: {
            SearchResponseEntitiesDialog
        },
        props: {
            value: {
                type: Boolean,
                default: false
            },
            brands: {
                type: Array,
                default: () => []
            },
            projectId: {
                type: String,
                default: null
            },
            initialEdited: {
                type: Array,
                default: () => []
            },
            initialEditedLoaded: {
                type: Boolean,
                default: false
            },
            initialFavorites: {
                type: Array,
                default: () => []
            },
            initialColors: {
                type: Object,
                default: () => ({})
            },
            resetLoading: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                managedBrandsDraft: [],
                favoriteSet: [],
                colorMap: {},
                colorMenuFor: null,
                colorDirty: false,
                activeColor: null,
                colorSwatches: [
                    ["#F44336", "#E91E63", "#9C27B0"],
                    ["#673AB7", "#3F51B5", "#2196F3"],
                    ["#03A9F4", "#00BCD4", "#009688"],
                    ["#4CAF50", "#8BC34A", "#CDDC39"],
                    ["#FFC107", "#FF9800", "#FF5722"],
                    ["#795548", "#607D8B", "#9E9E9E"]
                ],
                brandSearchQuery: "",
                brandsPage: 1,
                brandsPerPage: 12,
                mergeSelectionSource: null,
                mergeConfirmTarget: null,
                mergeConfirmDialog: false,
                addMentionDialog: false,
                addMentionTarget: null,
                addMentionValue: "",
                domainDialog: false,
                domainTarget: null,
                domainValue: "",
                resetConfirmDialog: false,
                canonicalEditSource: null,
                canonicalEditValue: "",
                searchEntitiesDialog: false
            }
        },
        computed: {
            dialogProxy: {
                get() {
                    return this.value
                },
                set(v) {
                    this.$emit("input", v)
                }
            },
            filteredManagedBrands() {
                const q = this.normalizeMention(this.brandSearchQuery).toLowerCase()
                if (!q) return this.managedBrandsDraft || []
                return (this.managedBrandsDraft || []).filter((brand) => {
                    const canonical = this.normalizeMention(brand?.canonicalName).toLowerCase()
                    const domain = this.normalizeDomain(brand?.brand_domain).toLowerCase()
                    const mentions = Array.isArray(brand?.mentions) ? brand.mentions.map((m) => this.normalizeMention(m).toLowerCase()) : []
                    const blacklisted = Array.isArray(brand?.blacklisted) ? brand.blacklisted.map((m) => this.normalizeMention(m).toLowerCase()) : []
                    return canonical.includes(q) || domain.includes(q) || mentions.some((m) => m.includes(q)) || blacklisted.some((m) => m.includes(q))
                })
            },
            selectedSourceBrand() {
                if (!this.mergeSelectionSource) return null
                return this.getManagedBrandByCanonical(this.mergeSelectionSource)
            },
            filteredManagedBrandsWithoutSelected() {
                if (!this.mergeSelectionSource) return this.filteredManagedBrands
                const selected = this.normalizeMention(this.mergeSelectionSource)
                return (this.filteredManagedBrands || []).filter((b) => this.normalizeMention(b?.canonicalName) !== selected)
            },
            totalBrandPages() {
                const total = this.filteredManagedBrandsWithoutSelected.length
                return Math.max(1, Math.ceil(total / this.brandsPerPage))
            },
            paginatedManagedBrands() {
                const start = (this.brandsPage - 1) * this.brandsPerPage
                return this.filteredManagedBrandsWithoutSelected.slice(start, start + this.brandsPerPage)
            },
            brandsToRender() {
                if (this.selectedSourceBrand) {
                    return [this.selectedSourceBrand, ...this.paginatedManagedBrands]
                }
                return this.paginatedManagedBrands
            }
        },
        watch: {
            value(next) {
                if (next) {
                    this.initFavoritesAndColors()
                    this.initializeDraft()
                }
            },
            initialFavorites() {
                this.initFavoritesAndColors()
            },
            initialColors() {
                this.initFavoritesAndColors()
            },
            brandSearchQuery() {
                this.brandsPage = 1
            },
            filteredManagedBrands() {
                if (this.brandsPage > this.totalBrandPages) {
                    this.brandsPage = this.totalBrandPages
                }
            }
        },
        methods: {
            closeDialog() {
                this.$emit("input", false)
            },
            getStorageKey() {
                return this.projectId ? `ai_suite_managed_brands_${this.projectId}` : null
            },
            deepClone(value) {
                return JSON.parse(JSON.stringify(value))
            },
            normalizeMention(value) {
                return String(value || "").trim()
            },
            normalizeDomain(value) {
                return String(value || "")
                    .trim()
                    .toLowerCase()
                    .replace(/^https?:\/\//, "")
                    .replace(/^www\./, "")
                    .split(/[/?#]/)[0]
            },
            getBrandVisibility(entry) {
                const candidates = [entry?.responsesWithMention, entry?.visibility, entry?.mentionCount, entry?.count]
                for (const raw of candidates) {
                    const n = Number(raw)
                    if (Number.isFinite(n)) return n
                }
                return 0
            },
            sortManagedBrands(rows) {
                return (Array.isArray(rows) ? rows : []).sort((a, b) => {
                    const byVisibility = this.getBrandVisibility(b) - this.getBrandVisibility(a)
                    if (byVisibility !== 0) return byVisibility
                    return this.normalizeMention(a?.canonicalName).localeCompare(this.normalizeMention(b?.canonicalName))
                })
            },
            getBrandAccentColor(brand) {
                const key = this.normalizeMention(brand?.canonicalName || brand?.brand_name || brand?.name).toLowerCase()
                return resolveBrandColor(key, this.colorMap)
            },
            brandColorKey(brand) {
                return this.normalizeMention(brand?.canonicalName || brand?.brand_name || brand?.name).toLowerCase()
            },
            isFavorite(brand) {
                const key = this.brandColorKey(brand)
                return !!key && this.favoriteSet.includes(key)
            },
            toggleFavorite(brand) {
                const key = this.brandColorKey(brand)
                if (!key) return
                const idx = this.favoriteSet.indexOf(key)
                if (idx > -1) this.favoriteSet.splice(idx, 1)
                else this.favoriteSet.push(key)
                this.emitFavorites()
            },
            setBrandColor(brand, color) {
                const key = this.brandColorKey(brand)
                if (!key || !color) return
                const val = String(color).trim()
                if (!val) return
                if (this.colorMap[key] === val) return
                // Nur lokal aktualisieren (Live-Vorschau im Dialog); Emit/Save erst beim Schliessen des Menues
                this.$set(this.colorMap, key, val)
                this.colorDirty = true
            },
            resetBrandColor(brand) {
                const key = this.brandColorKey(brand)
                if (!key) return
                if (Object.prototype.hasOwnProperty.call(this.colorMap, key)) {
                    this.$delete(this.colorMap, key)
                    this.colorDirty = true
                }
                // Picker auf die automatische Farbe zurücksetzen
                this.activeColor = this.getBrandAccentColor(brand)
            },
            onColorMenuChange(v, brand) {
                if (v) {
                    this.colorMenuFor = brand.canonicalName
                    // Stabilen Startwert setzen, damit der Picker keinen Feedback-Loop bekommt
                    this.activeColor = this.getBrandAccentColor(brand)
                } else {
                    this.closeColorMenu()
                }
            },
            closeColorMenu() {
                this.colorMenuFor = null
                if (this.colorDirty) {
                    this.colorDirty = false
                    this.emitFavorites()
                }
            },
            emitFavorites() {
                this.$emit("update-favorites", {
                    favorites: Array.from(new Set(this.favoriteSet.filter(Boolean))),
                    colors: { ...this.colorMap }
                })
            },
            initFavoritesAndColors() {
                this.favoriteSet = (Array.isArray(this.initialFavorites) ? this.initialFavorites : [])
                    .map((v) =>
                        String(v || "")
                            .trim()
                            .toLowerCase()
                    )
                    .filter(Boolean)
                const map = {}
                if (this.initialColors && typeof this.initialColors === "object") {
                    Object.keys(this.initialColors).forEach((k) => {
                        const key = String(k || "")
                            .trim()
                            .toLowerCase()
                        const val = this.initialColors[k]
                        if (key && typeof val === "string" && val.trim()) map[key] = val.trim()
                    })
                }
                this.colorMap = map
            },
            getBrandCardStyle(brand) {
                const color = this.getBrandAccentColor(brand)
                return {
                    borderLeft: `4px solid ${color}`
                }
            },
            extractMentionValue(value) {
                if (typeof value === "string") return value
                if (value?.text?.content) return value.text.content
                if (value?.content) return value.content
                if (value?.name) return value.name
                return ""
            },
            loadFromStorage() {
                try {
                    const key = this.getStorageKey()
                    if (!key) return null
                    const raw = localStorage.getItem(key)
                    if (!raw) return null
                    const parsed = JSON.parse(raw)
                    return Array.isArray(parsed) ? parsed : null
                } catch {
                    return null
                }
            },
            saveToStorage(payload) {
                try {
                    const key = this.getStorageKey()
                    if (!key) return
                    localStorage.setItem(key, JSON.stringify(payload || []))
                } catch {
                    // ignore
                }
            },
            buildFromBrands() {
                const byCanonical = new Map()
                ;(this.brands || []).forEach((b) => {
                    const canonical = this.normalizeMention(b?.canonicalName || b?.brand_name || b?.name)
                    if (!canonical) return
                    const mentions = Array.isArray(b?.mentions) ? b.mentions.map((m) => this.normalizeMention(this.extractMentionValue(m))).filter(Boolean) : []
                    const blacklisted = Array.isArray(b?.blacklisted) ? b.blacklisted.map((m) => this.normalizeMention(m)).filter(Boolean) : []
                    const responsesWithMention = this.getBrandVisibility(b)
                    const brand_domain = this.normalizeDomain(b?.brand_domain || b?.metadata?.officialWebsite || b?.metadata?.website)

                    if (!byCanonical.has(canonical)) {
                        byCanonical.set(canonical, {
                            canonicalName: canonical,
                            mentions: Array.from(new Set([canonical, ...mentions])),
                            blacklisted: Array.from(new Set(blacklisted)),
                            brand_domain,
                            responsesWithMention
                        })
                        return
                    }

                    const prev = byCanonical.get(canonical)
                    prev.mentions = Array.from(new Set([...(prev.mentions || []), canonical, ...mentions]))
                    prev.blacklisted = Array.from(new Set([...(prev.blacklisted || []), ...blacklisted]))
                    if (!prev.brand_domain && brand_domain) prev.brand_domain = brand_domain
                    prev.responsesWithMention = Math.max(this.getBrandVisibility(prev), responsesWithMention)
                })
                return this.sortManagedBrands(Array.from(byCanonical.values()))
            },
            normalizeManagedDraftRow(entry) {
                const canonicalName = this.normalizeMention(entry?.canonicalName || entry?.brand_name || entry?.name)
                if (!canonicalName) return null
                const mentions = Array.from(new Set((Array.isArray(entry?.mentions) ? entry.mentions : []).map((m) => this.normalizeMention(this.extractMentionValue(m))).filter(Boolean)))
                const blacklisted = Array.from(new Set((Array.isArray(entry?.blacklisted) ? entry.blacklisted : []).map((m) => this.normalizeMention(m)).filter(Boolean)))
                const responsesWithMention = this.getBrandVisibility(entry)
                const brand_domain = this.normalizeDomain(entry?.brand_domain || entry?.metadata?.officialWebsite || entry?.metadata?.website)

                return {
                    canonicalName,
                    mentions,
                    blacklisted,
                    brand_domain,
                    responsesWithMention,
                    deleted: entry?.deleted === true
                }
            },
            mergeBrandsWithEdits(baseBrands, editedRows) {
                const map = new Map()

                ;(Array.isArray(baseBrands) ? baseBrands : []).forEach((row) => {
                    const normalized = this.normalizeManagedDraftRow(row)
                    if (!normalized) return
                    const key = normalized.canonicalName.toLowerCase()
                    map.set(key, {
                        canonicalName: normalized.canonicalName,
                        mentions: Array.from(new Set([normalized.canonicalName, ...(normalized.mentions || [])])),
                        blacklisted: normalized.blacklisted || [],
                        brand_domain: normalized.brand_domain || "",
                        responsesWithMention: normalized.responsesWithMention
                    })
                })
                ;(Array.isArray(editedRows) ? editedRows : []).forEach((row) => {
                    const rowHasVisibility = row?.responsesWithMention != null || row?.visibility != null || row?.mentionCount != null || row?.count != null
                    const normalized = this.normalizeManagedDraftRow(row)
                    if (!normalized) return
                    const key = normalized.canonicalName.toLowerCase()

                    if (normalized.deleted) {
                        map.delete(key)
                        return
                    }

                    const existing = map.get(key)
                    map.set(key, {
                        canonicalName: normalized.canonicalName,
                        mentions: Array.from(new Set([normalized.canonicalName, ...(normalized.mentions || [])])),
                        blacklisted: normalized.blacklisted || [],
                        brand_domain: normalized.brand_domain || this.normalizeDomain(existing?.brand_domain),
                        responsesWithMention: rowHasVisibility ? normalized.responsesWithMention : this.getBrandVisibility(existing)
                    })
                })

                return this.sortManagedBrands(Array.from(map.values()))
            },
            initializeDraft() {
                const fromProp = Array.isArray(this.initialEdited) ? this.deepClone(this.initialEdited) : null
                const fromStorage = this.loadFromStorage()
                const fromBrands = this.buildFromBrands()
                let initial = null

                if (this.initialEditedLoaded) {
                    initial = this.mergeBrandsWithEdits(fromBrands, Array.isArray(fromProp) ? fromProp : [])
                } else {
                    const preferredEdits = (Array.isArray(fromProp) && fromProp.length ? fromProp : null) || fromStorage || []
                    initial = this.mergeBrandsWithEdits(fromBrands, preferredEdits)
                }

                this.managedBrandsDraft = this.deepClone(initial)
                this.brandSearchQuery = ""
                this.brandsPage = 1
                this.mergeSelectionSource = null
                this.mergeConfirmTarget = null
                this.mergeConfirmDialog = false
                this.addMentionDialog = false
                this.addMentionTarget = null
                this.addMentionValue = ""
                this.domainDialog = false
                this.domainTarget = null
                this.domainValue = ""
                this.resetConfirmDialog = false
                this.canonicalEditSource = null
                this.canonicalEditValue = ""
                this.searchEntitiesDialog = false
                this.colorMenuFor = null
            },
            getManagedBrandByCanonical(canonicalName) {
                const normalized = this.normalizeMention(canonicalName)
                return (this.managedBrandsDraft || []).find((b) => this.normalizeMention(b?.canonicalName) === normalized) || null
            },
            isCanonicalEditing(canonicalName) {
                return this.normalizeMention(this.canonicalEditSource) === this.normalizeMention(canonicalName)
            },
            startCanonicalEdit(canonicalName) {
                const current = this.normalizeMention(canonicalName)
                this.canonicalEditSource = current
                this.canonicalEditValue = current
            },
            cancelCanonicalEdit() {
                this.canonicalEditSource = null
                this.canonicalEditValue = ""
            },
            applyCanonicalRename() {
                const source = this.normalizeMention(this.canonicalEditSource)
                const next = this.normalizeMention(this.canonicalEditValue)
                if (!source || !next) {
                    this.cancelCanonicalEdit()
                    return
                }

                const targetBrand = this.getManagedBrandByCanonical(source)
                if (!targetBrand) {
                    this.cancelCanonicalEdit()
                    return
                }

                const duplicate = (this.managedBrandsDraft || []).find(
                    (b) => this.normalizeMention(b?.canonicalName).toLowerCase() === next.toLowerCase() && this.normalizeMention(b?.canonicalName) !== source
                )
                if (duplicate) {
                    this.mergeManagedBrands(source, duplicate.canonicalName)
                    this.cancelCanonicalEdit()
                    return
                }

                if (source !== next) {
                    const mentionSet = new Set((targetBrand.mentions || []).map((m) => this.normalizeMention(m)).filter(Boolean))
                    mentionSet.add(source)
                    mentionSet.add(next)
                    targetBrand.canonicalName = next
                    targetBrand.mentions = Array.from(mentionSet)

                    if (this.normalizeMention(this.mergeSelectionSource) === source) {
                        this.mergeSelectionSource = next
                    }
                    if (this.normalizeMention(this.mergeConfirmTarget) === source) {
                        this.mergeConfirmTarget = next
                    }

                    this.sortManagedBrands(this.managedBrandsDraft)
                }

                this.cancelCanonicalEdit()
            },
            removeManagedMention(canonicalName, mention) {
                const brand = this.getManagedBrandByCanonical(canonicalName)
                if (!brand) return
                const token = this.normalizeMention(mention)
                if (!token) return
                brand.mentions = (brand.mentions || []).filter((m) => this.normalizeMention(m) !== token)
                const nextBlacklisted = new Set([...(brand.blacklisted || []), token])
                brand.blacklisted = Array.from(nextBlacklisted)
            },
            splitManagedBrandByMention(canonicalName, mention) {
                const brand = this.getManagedBrandByCanonical(canonicalName)
                if (!brand) return
                const token = this.normalizeMention(mention)
                if (!token) return
                if (this.managedBrandsDraft.some((b) => this.normalizeMention(b.canonicalName) === token)) return

                brand.mentions = (brand.mentions || []).filter((m) => this.normalizeMention(m) !== token)
                this.managedBrandsDraft.push({
                    canonicalName: token,
                    mentions: [token],
                    blacklisted: [],
                    brand_domain: "",
                    responsesWithMention: 0
                })
                this.sortManagedBrands(this.managedBrandsDraft)
            },
            addManagedMention(canonicalName, mentionValue = null) {
                const brand = this.getManagedBrandByCanonical(canonicalName)
                if (!brand) return

                const raw = this.normalizeMention(mentionValue)
                if (!raw) return

                const alreadyExists = (brand.mentions || []).some((m) => this.normalizeMention(m).toLowerCase() === raw.toLowerCase())
                if (alreadyExists) {
                    brand._newMention = ""
                    return
                }

                brand.mentions = Array.from(new Set([...(brand.mentions || []), raw]))
                brand.blacklisted = (brand.blacklisted || []).filter((m) => this.normalizeMention(m).toLowerCase() !== raw.toLowerCase())
            },
            openAddMentionDialog(canonicalName) {
                this.addMentionTarget = this.normalizeMention(canonicalName)
                this.addMentionValue = ""
                this.addMentionDialog = true
            },
            cancelAddMention() {
                this.addMentionDialog = false
                this.addMentionTarget = null
                this.addMentionValue = ""
            },
            confirmAddMention() {
                const target = this.normalizeMention(this.addMentionTarget)
                const value = this.normalizeMention(this.addMentionValue)
                if (!target || !value) return
                this.addManagedMention(target, value)
                this.cancelAddMention()
            },
            openDomainDialog(canonicalName) {
                const target = this.normalizeMention(canonicalName)
                const brand = this.getManagedBrandByCanonical(target)
                if (!brand) return
                this.domainTarget = target
                this.domainValue = this.normalizeDomain(brand.brand_domain)
                this.domainDialog = true
            },
            cancelDomain() {
                this.domainDialog = false
                this.domainTarget = null
                this.domainValue = ""
            },
            confirmDomain() {
                const target = this.normalizeMention(this.domainTarget)
                const brand = this.getManagedBrandByCanonical(target)
                if (!brand) {
                    this.cancelDomain()
                    return
                }
                brand.brand_domain = this.normalizeDomain(this.domainValue)
                this.cancelDomain()
            },
            clearStorage() {
                try {
                    const key = this.getStorageKey()
                    if (!key) return
                    localStorage.removeItem(key)
                } catch {
                    // ignore
                }
            },
            openResetConfirmDialog() {
                this.resetConfirmDialog = true
            },
            confirmResetBrands() {
                this.resetConfirmDialog = false
                this.clearStorage()
                this.$emit("reset-brands")
            },
            isMergeSource(canonicalName) {
                return this.normalizeMention(this.mergeSelectionSource) === this.normalizeMention(canonicalName)
            },
            startMergeSelection(canonicalName) {
                const next = this.normalizeMention(canonicalName)
                const current = this.normalizeMention(this.mergeSelectionSource)
                if (current && current === next) {
                    this.cancelMergeSelection()
                    return
                }
                this.mergeSelectionSource = next
                this.mergeConfirmTarget = null
                this.mergeConfirmDialog = false
            },
            cancelMergeSelection() {
                this.mergeSelectionSource = null
                this.mergeConfirmTarget = null
                this.mergeConfirmDialog = false
            },
            requestMergeWithTarget(canonicalName) {
                const target = this.normalizeMention(canonicalName)
                const source = this.normalizeMention(this.mergeSelectionSource)
                if (!source || !target || source === target) return
                this.mergeConfirmTarget = target
                this.mergeConfirmDialog = true
            },
            cancelMergeConfirm() {
                this.mergeConfirmDialog = false
                this.mergeConfirmTarget = null
            },
            mergeManagedBrands(sourceCanonical, targetCanonical) {
                const sourceName = this.normalizeMention(sourceCanonical)
                const targetName = this.normalizeMention(targetCanonical)
                if (!sourceName || !targetName || sourceName === targetName) return

                const sourceIdx = this.managedBrandsDraft.findIndex((b) => this.normalizeMention(b.canonicalName) === sourceName)
                const targetIdx = this.managedBrandsDraft.findIndex((b) => this.normalizeMention(b.canonicalName) === targetName)
                if (sourceIdx < 0 || targetIdx < 0 || sourceIdx === targetIdx) return

                const source = this.managedBrandsDraft[sourceIdx]
                const target = this.managedBrandsDraft[targetIdx]
                target.mentions = Array.from(new Set([...(target.mentions || []), ...(source.mentions || [])]))
                target.blacklisted = Array.from(new Set([...(target.blacklisted || []), ...(source.blacklisted || [])]))
                if (!target.brand_domain && source.brand_domain) target.brand_domain = source.brand_domain
                target.responsesWithMention = Math.max(this.getBrandVisibility(target), this.getBrandVisibility(source))
                this.managedBrandsDraft.splice(sourceIdx, 1)
                this.sortManagedBrands(this.managedBrandsDraft)
            },
            confirmMergeSelection() {
                const source = this.normalizeMention(this.mergeSelectionSource)
                const target = this.normalizeMention(this.mergeConfirmTarget)
                if (!source || !target || source === target) return
                this.mergeManagedBrands(source, target)
                this.cancelMergeSelection()
            },
            openSearchEntitiesDialog() {
                this.searchEntitiesDialog = true
            },
            isEntityAlreadyManaged(entityName) {
                const token = this.normalizeMention(entityName).toLowerCase()
                if (!token) return false
                return (this.managedBrandsDraft || []).some((b) => {
                    if (this.normalizeMention(b?.canonicalName).toLowerCase() === token) return true
                    const mentions = Array.isArray(b?.mentions) ? b.mentions : []
                    return mentions.some((m) => this.normalizeMention(m).toLowerCase() === token)
                })
            },
            onSearchEntitiesAddBrands(brands) {
                if (!Array.isArray(brands) || !brands.length) return
                brands.forEach((b) => {
                    const name = this.normalizeMention(b?.canonicalName)
                    if (!name) return
                    if (this.isEntityAlreadyManaged(name)) return
                    this.managedBrandsDraft.push({
                        canonicalName: name,
                        mentions: Array.isArray(b?.mentions) && b.mentions.length ? Array.from(new Set(b.mentions.map((m) => this.normalizeMention(m)).filter(Boolean))) : [name],
                        blacklisted: Array.isArray(b?.blacklisted) ? b.blacklisted.map((m) => this.normalizeMention(m)).filter(Boolean) : [],
                        brand_domain: this.normalizeDomain(b?.brand_domain || ""),
                        responsesWithMention: Number(b?.responsesWithMention) || 0
                    })
                })
                this.sortManagedBrands(this.managedBrandsDraft)
            },
            onSearchEntitiesAddMergedBrand(payload) {
                const canonical = this.normalizeMention(payload?.canonicalName)
                if (!canonical) return
                const mentionSet = new Set([canonical, ...(payload?.mentions || []).map((m) => this.normalizeMention(m)).filter(Boolean)])
                const existing = this.getManagedBrandByCanonical(canonical)
                const totalCount = Number(payload?.responsesWithMention) || 0

                if (existing) {
                    existing.mentions = Array.from(new Set([...(existing.mentions || []), ...Array.from(mentionSet)]))
                    existing.blacklisted = (existing.blacklisted || []).filter((b) => !mentionSet.has(this.normalizeMention(b)))
                } else {
                    this.managedBrandsDraft.push({
                        canonicalName: canonical,
                        mentions: Array.from(mentionSet),
                        blacklisted: [],
                        brand_domain: "",
                        responsesWithMention: totalCount
                    })
                }
                this.sortManagedBrands(this.managedBrandsDraft)
            },
            saveChanges() {
                const cleaned = (this.managedBrandsDraft || [])
                    .map((b) => ({
                        canonicalName: this.normalizeMention(b.canonicalName),
                        mentions: Array.from(new Set((b.mentions || []).map((m) => this.normalizeMention(m)).filter(Boolean))),
                        blacklisted: Array.from(new Set((b.blacklisted || []).map((m) => this.normalizeMention(m)).filter(Boolean))),
                        brand_domain: this.normalizeDomain(b.brand_domain || "")
                    }))
                    .filter((b) => b.canonicalName)

                this.saveToStorage(cleaned)
                this.$emit("save", cleaned)
            }
        }
    }
</script>

<style scoped>
    .manage-brands-card {
        overflow: hidden;
    }
    .manage-brand-card {
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.7);
    }
    .manage-brands-list {
        max-height: 52vh;
        overflow-y: auto;
        padding-right: 4px;
    }
    .brand-header {
        gap: 12px;
    }
    .brand-header-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 8px;
    }
    .mentions-count-chip {
        background: rgba(0, 0, 0, 0.02);
    }
    .manage-brand--selected-source {
        border: 2px solid var(--v-primary-base) !important;
        box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.12);
        background: rgba(25, 118, 210, 0.04);
    }
    .brand-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        display: inline-block;
        margin-right: 8px;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
        flex: 0 0 auto;
    }
    .brand-title {
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 320px;
    }
    .brand-color-swatch {
        width: 14px;
        height: 14px;
        border-radius: 4px;
        display: inline-block;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
    }
    .manage-mention-chip {
        min-height: 28px;
        border-radius: 16px;
        background: rgba(0, 0, 0, 0.01);
    }
    .merge-action-btn {
        min-width: 152px;
        height: 32px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.01em;
        text-transform: none;
    }
    .merge-action-btn--target {
        box-shadow: 0 2px 6px rgba(25, 118, 210, 0.25);
    }
    .split-mention-btn {
        height: 24px;
        padding-right: 5px;
        padding: 4px !important;
        font-size: 11px;
        letter-spacing: 0.01em;
        text-transform: none;
    }
    .brand-domain-row {
        max-width: 740px;
    }
    .brand-domain-chip {
        min-height: 30px;
        border-style: dashed;
    }
    .clickable-chip {
        cursor: pointer;
    }
    .add-mention-action-btn {
        min-width: 128px;
    }
    .brand-domain-field {
        max-width: 380px;
    }
</style>
